import _ from 'underscore';
import ko from 'knockout';
import path from 'path-browserify';
import Promise from 'bluebird';

function validatorDecorator(
  validator,
  reasonText,
  file,
  context
) {
  return Promise.resolve(validator(file)).then(returnedError =>
    new Promise((resolve, reject) => {
      if (returnedError) {
        reject(new Error(_.isString(returnedError) ? returnedError : reasonText));
      } else {
        resolve(file);
      }
    })).bind(context);
}

export default class ViewModel {
  constructor({
    i18n,
    allowedFileExtensions,
    validateFileExtensionsFailedMessage,
    validator,
    getFileObject,
    getFileObjects,
    trigger,
    displayFilePath,
    enableMultiple,
  }) {
    this.i18n = i18n;
    this.allowedFileExtensions = _.map(allowedFileExtensions, ext => ext.toUpperCase());
    this.validateFileExtensionsFailedMessage = validateFileExtensionsFailedMessage;

    this.validator = validator;
    this.getFileObject = getFileObject;
    this.getFileObjects = getFileObjects;
    this.trigger = trigger;
    this.enableMultiple = enableMultiple;

    this.filePath = ko.observable('');
    this.errors = ko.observableArray();
    this.hasErrors = ko.computed(() => this.errors().length > 0, this);
    this.displayFilePath = ko.computed(() => {
      const fullPath = this.filePath();

      return _.result({
        full: fullPath,
        filename: () => fullPath.replace(/^.*[\\\/]/, ''), //eslint-disable-line
      }, displayFilePath);
    });

    if (enableMultiple) {
      // use change event when multiple is enabled.
      // value subscribe doens't work when first selected file doesn't change.
      this.onInputChange = () => {
        this.handleInputChange();
      };
    } else {
      this.filePath.subscribe((newFilePath) => {
        this.handleInputChange(newFilePath);
      });
    }
  }

  handleInputChange(newFilePath) {
    // for backward compatibility, here still use getFileObject to get selected file.
    // otherwise there are many feature test codes needing fix.
    let files = [];
    if (this.enableMultiple) {
      files = this.getFileObjects();
    } else {
      const file = this.getFileObject();
      if (file) {
        files.push(file);
      }
    }

    this.resetStatus();

    this.trigger('changed', newFilePath);

    if (_.isEmpty(files)) {
      this.trigger('cleared');
    } else {
      const validatingPromiseArr = [];
      _.each(files, (file) => {
        validatingPromiseArr.push(this.validateExtension(file).then(() => {
          this.validateFile(file);
        }));
      });

      Promise.all(validatingPromiseArr)
        .then(() => this.trigger('validated'))
        .catch((reason) => {
          this.validationDidFail(reason);
        });
    }
  }

  get extensionValidator() {
    return (file) => {
      if (_.isEmpty(this.allowedFileExtensions)) {
        return false;
      }

      const extension = path.extname(file.name).toUpperCase();
      if (_.indexOf(this.allowedFileExtensions, extension) === -1) {
        this.trigger('error.extension');

        return this.validateFileExtensionsFailedMessage || true;
      }

      return null;
    };
  }

  validateExtension(file) {
    return validatorDecorator(
      this.extensionValidator.bind(this),
      this.i18n.getString('FilePicker_FileExtensionInvalid', { allowedFileExtensions: this.allowedFileExtensions }),
      file,
      this
    );
  }

  validateFile(file) {
    return validatorDecorator(this.validator, this.i18n.getString('FilePicker_FileInvalid'), file, this);
  }

  validationDidFail(reason) {
    this.errors.push(reason.message);
  }

  resetStatus() {
    this.errors([]);
  }
}
